import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Package, ChevronLeft, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PhaseStepper } from "./pulllist/PhaseStepper";
import { PullPhase } from "./pulllist/PullPhase";
import { PrintPhase } from "./pulllist/PrintPhase";
import { PackPhase } from "./pulllist/PackPhase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const flowKey = (id) => `pullflow_${id}`;

export default function OrdersPage({ user, getAuthHeader, onBack }) {
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [flow, setFlow] = useState({ phase: "pull", printed: false, packed: {} });
  const [previews, setPreviews] = useState({});
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [printUrls, setPrintUrls] = useState([]);

  const fetchAssignment = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/orders/my-assignment`, getAuthHeader());
      setAssignment(data.assignment);
      if (data.assignment?.id) {
        try {
          const raw = localStorage.getItem(flowKey(data.assignment.id));
          if (raw) setFlow((f) => ({ ...f, ...JSON.parse(raw) }));
        } catch {}
      }
    } catch (err) {
      toast.error("Failed to load pull list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAssignment(); }, [fetchAssignment]);

  const updateFlow = (patch) => {
    setFlow((prev) => {
      const next = { ...prev, ...patch };
      if (assignment?.id) {
        try { localStorage.setItem(flowKey(assignment.id), JSON.stringify(next)); } catch {}
      }
      return next;
    });
  };

  const goTo = (phase) => { updateFlow({ phase }); window.scrollTo({ top: 0 }); };

  const handleTogglePull = async (item) => {
    const isPulled = item.pulled;
    try {
      const path = isPulled ? "reset-pulled" : "mark-pulled";
      await axios.post(`${API}/orders/my-assignment/${path}`, [item.id], getAuthHeader());
      setAssignment((prev) => prev && ({
        ...prev,
        items: prev.items.map((i) =>
          i.id === item.id ? { ...i, pulled: !isPulled, pulled_at: isPulled ? null : new Date().toISOString() } : i
        ),
      }));
    } catch (err) {
      toast.error("Failed to update item");
    }
  };

  const handleTogglePacked = (itemId) => {
    updateFlow({ packed: { ...flow.packed, [itemId]: !flow.packed[itemId] } });
  };

  const handleFinish = async () => {
    if (!assignment) return;
    const unpulled = (assignment.items || []).filter((i) => !i.pulled).length;
    if (unpulled > 0) {
      const ok = window.confirm(`${unpulled} item${unpulled > 1 ? "s" : ""} never got pulled. Finish anyway?`);
      if (!ok) return;
    }
    setCompleting(true);
    try {
      await axios.post(`${API}/orders/complete/${assignment.id}`, {}, getAuthHeader());
      try { localStorage.removeItem(flowKey(assignment.id)); } catch {}
      toast.success("Pull list finished!");
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to finish");
    } finally {
      setCompleting(false);
    }
  };

  const fetchLabelBlobUrl = async (labelId) => {
    const headers = getAuthHeader().headers;
    const res = await fetch(`${API}/orders/labels/${labelId}/preview`, { headers });
    if (!res.ok) throw new Error("Failed to fetch label");
    return URL.createObjectURL(await res.blob());
  };

  const loadPreview = async (labelId) => {
    try {
      const url = await fetchLabelBlobUrl(labelId);
      setPreviews((prev) => ({ ...prev, [labelId]: url }));
      return url;
    } catch {
      setPreviews((prev) => ({ ...prev, [labelId]: null }));
      return null;
    }
  };



  const printAllLabels = async () => {
    const allLabels = assignment?.labels || [];
    if (allLabels.length === 0) { toast.error("No labels to print"); return; }
    setPrinting(true);
    try {
      const urls = await Promise.all(allLabels.map((l) => previews[l.id] || fetchLabelBlobUrl(l.id)));
      setPrintUrls(urls);
      document.body.classList.add("printing-labels");
      await new Promise((r) => setTimeout(r, 800));
      const cleanup = () => {
        document.body.classList.remove("printing-labels");
        setPrintUrls([]);
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);
      window.print();
      updateFlow({ printed: true });
    } catch {
      toast.error("Failed to load labels for printing");
      document.body.classList.remove("printing-labels");
      setPrintUrls([]);
    } finally {
      setPrinting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-white/30 animate-spin" />
        <p className="text-sm text-white/40 mt-3">Loading pull list...</p>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="flex flex-col items-center justify-center py-20" data-testid="orders-empty">
        <Package className="w-10 h-10 text-white/20 mb-3" />
        <p className="text-sm text-white/40">No pull list assigned to you</p>
        <button onClick={onBack} className="mt-4 text-sm text-[#00D4FF] hover:underline" data-testid="orders-back-empty-btn">Back to Dashboard</button>
      </div>
    );
  }

  const items = assignment.items || [];
  const labels = assignment.labels || [];
  const pulledCount = items.filter((i) => i.pulled).length;
  const done = {
    pull: items.length > 0 && pulledCount === items.length,
    print: flow.printed,
    pack: false,
  };

  return (
    <div className="space-y-4 pb-28" data-testid="orders-page">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-white/50 hover:text-white/80 transition-colors" data-testid="orders-back-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-white/90">Pull List</h2>
          <p className="text-xs text-white/40 truncate" data-testid="pull-list-summary">
            {items.length} items · {labels.length} labels
            {assignment.since && ` · ${assignment.since}`}
            {assignment.until && assignment.until !== assignment.since && ` to ${assignment.until}`}
          </p>
        </div>
      </div>

      <PhaseStepper phase={flow.phase} done={done} onSelect={goTo} />

      {assignment.notes && (
        <div className="p-3 rounded-lg bg-[#8B5CF6]/5 border border-[#8B5CF6]/15" data-testid="assignment-notes">
          <p className="text-xs text-[#8B5CF6]/80">{assignment.notes}</p>
        </div>
      )}

      {flow.phase === "pull" && (
        <PullPhase items={items} onTogglePull={handleTogglePull} onImage={setLightboxUrl} onNext={() => goTo("print")} />
      )}
      {flow.phase === "print" && (
        <PrintPhase
          labels={labels}
          previews={previews}
          loadPreview={loadPreview}
          onPrint={printAllLabels}
          printing={printing}
          printed={flow.printed}
          onZoom={setLightboxUrl}
          onBack={() => goTo("pull")}
          onNext={() => goTo("pack")}
        />
      )}
      {flow.phase === "pack" && (
        <PackPhase
          items={items}
          labels={labels}
          packed={flow.packed || {}}
          onTogglePacked={handleTogglePacked}
          onImage={setLightboxUrl}
          previews={previews}
          loadPreview={loadPreview}
          onBack={() => goTo("print")}
          onFinish={handleFinish}
          finishing={completing}
        />
      )}

      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
          data-testid="image-lightbox"
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:bg-white/20 transition-colors"
            onClick={() => setLightboxUrl(null)}
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img src={lightboxUrl} alt="Label" className="max-w-full max-h-[85vh] object-contain rounded-lg bg-white" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {printUrls.length > 0 && createPortal(
        <div id="label-print-root">
          {printUrls.map((url, i) => (
            <div className="label-page" key={i}><img src={url} alt={`Label ${i + 1}`} /></div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
